import { type AxiosRequestConfig } from "axios";
import {
  api,
  type AdminTextRejectionDto,
  type AdminTextRejectionListResponseDto,
  type GetTextRejectionsParams,
  type ModerationOverviewDto,
  type ResolveTextAppealDto,
  type AdminReportListResponseDto,
  type GetReportsParams,
  type AdminReportDto,
  type AdminReportResponseDto,
} from "@scoutus/sports-metrics";
import { dedupeAsync } from "../utils/dedupeAsync";
import {
  adminAxiosClient,
  setAdminAuthToken,
} from "./adminAxiosClient";

/** @deprecated Use setAdminAuthToken — kept for call-site compatibility. */
export const setModerationAuthToken = setAdminAuthToken;

export type ReportResolveDecision =
  "reviewing" | "open" | "actioned" | "dismissed";

/** Extended list filters before sports-metrics regenerates claimedByMe / countOnly. */
export type GetReportsParamsExtended = GetReportsParams & {
  claimedByMe?: boolean;
  /** Raw COUNT only — skips consolidation and row enrichment. */
  countOnly?: boolean;
  /** Intake: user | ai | legacy (unified Reports queue). */
  source?: "user" | "ai" | "legacy";
  /** Case-insensitive match on name, target type, or reason (full list). */
  search?: string;
  /** created_at (default), resolved_at, reporter_count, or moderation_score (media AI). */
  sort?: "created_at" | "resolved_at" | "reporter_count" | "moderation_score";
  /**
   * Sort direction. created_at / resolved_at: desc = newest first.
   * reporter_count: desc = most reporters first.
   * moderation_score: desc = highest score first.
   */
  order?: "asc" | "desc";
};

export interface ResolveReportDto {
  decision: ReportResolveDecision;
  note?: string;
}

export type ReportEnforceAction =
  "remove_warn" | "remove_strike" | "remove_suspend" | "ban" | "dismiss";

export interface EnforceReportDto {
  action: ReportEnforceAction;
  policyTag?: string;
  note?: string;
  removeContent?: boolean;
  /**
   * When removing a post: also reject attached media that have open media
   * reports (AI / user). Default false for text-only takedowns.
   */
  rejectLinkedMedia?: boolean;
  /** Explicit media ids to reject when removing a post / media-on-post. */
  rejectMediaIds?: number[];
  strikeDelta?: number;
  /** 1 = 24h | 7 | 14 | 30 */
  suspendDays?: 1 | 7 | 14 | 30;
  /** Skip auto suspend from the strike ladder (requires overrideReason). */
  skipLadderLock?: boolean;
  overrideReason?: string;
}

export type AccountAppealStatus =
  | "pending"
  | "approved"
  | "denied"
  | "superseded";
export type AccountLockAction = "ban" | "suspend";

export interface AdminAccountAppealDto {
  id: number;
  userId: number;
  userName: string | null;
  userHandle?: string | null;
  userProfileImage?: string | null;
  userType: string | null;
  enforcementId: number;
  lockAction: AccountLockAction;
  suspendDays: number | null;
  expiresAt: string | Date | null;
  policyTag: string | null;
  statement: string;
  status: AccountAppealStatus;
  appealedAt: string | Date;
  resolvedAt: string | Date | null;
  resolvedBy: number | null;
  resolvedByName: string | null;
  resolveNote: string | null;
}

export interface AdminAccountAppealListResponse {
  data: AdminAccountAppealDto[];
  total: number;
  page: number;
  limit: number;
}

export interface GetAccountAppealsParams {
  page?: number;
  limit?: number;
  status?: AccountAppealStatus;
  userId?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface ResolveAccountAppealDto {
  decision: "approved" | "denied";
  note?: string;
}

export type PolicyRuleScope = "report" | "media";
export type PolicyRuleStatus = "active" | "archived";

/** Surfaces a report-scope rule may apply to. */
export type PolicyRuleTargetType = "user" | "post" | "media" | "message";

export type PolicyRuleGuidelinesByTarget = Partial<
  Record<PolicyRuleTargetType, string>
>;

export interface PolicyRuleDto {
  id: number;
  scope: PolicyRuleScope;
  value: string;
  label: string;
  tier: number;
  description: string | null;
  /** Report targets this rule applies to; empty for media-scope. */
  appliesTo?: PolicyRuleTargetType[];
  /** Per-surface guidelines; UI falls back to description. */
  guidelinesByTarget?: PolicyRuleGuidelinesByTarget;
  alignsWithTags?: string[];
  sortOrder: number;
  status: PolicyRuleStatus;
  archivedAt: string | Date | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface PolicyRuleListResponse {
  data: PolicyRuleDto[];
}

export interface GetPolicyRulesParams {
  scope?: PolicyRuleScope;
  includeArchived?: boolean;
}

export interface CreatePolicyRuleDto {
  scope?: PolicyRuleScope;
  value: string;
  label: string;
  tier: number;
  description?: string;
  appliesTo?: PolicyRuleTargetType[];
  guidelinesByTarget?: PolicyRuleGuidelinesByTarget;
  sortOrder?: number;
  alignsWithTags?: string[];
}

export interface UpdatePolicyRuleDto {
  label?: string;
  tier?: number;
  description?: string | null;
  appliesTo?: PolicyRuleTargetType[] | null;
  guidelinesByTarget?: PolicyRuleGuidelinesByTarget | null;
  sortOrder?: number;
  alignsWithTags?: string[] | null;
}

/** Linked post↔media moderation (avoid double-strike on the same post). */
export type RelatedModerationMediaItem = {
  mediaId: number;
  moderationStatus: string;
  openReportCount: number;
  enforcementPackage: string | null;
  strikeDelta: number;
  contentKey: string;
  latestOpenReportId?: number | null;
  latestEnforcementReportId?: number | null;
};

export type RelatedModerationPost = {
  postId: string;
  deleted: boolean;
  openReportCount: number;
  enforcementPackage: string | null;
  strikeDelta: number;
  contentKey: string;
  latestOpenReportId?: number | null;
  latestEnforcementReportId?: number | null;
};

export type RelatedModerationProfile = {
  userId: number;
  purpose: string;
  openReportCount: number;
  enforcementPackage: string | null;
  strikeDelta: number;
  contentKey: string;
  latestOpenReportId?: number | null;
  latestEnforcementReportId?: number | null;
};

export type RelatedModeration = {
  linkedStrikeApplied: boolean;
  summary: string | null;
  media?: RelatedModerationMediaItem[];
  post?: RelatedModerationPost | null;
  profile?: RelatedModerationProfile | null;
};

/** Extended fields returned by the API before sports-metrics regenerates. */
export type AdminReportExtended = AdminReportDto & {
  claimedByUserId?: number | null;
  claimedByName?: string | null;
  claimedByEmail?: string | null;
  claimedAt?: string | Date | null;
  targetItemOpenReportCount?: number;
  reporterCount?: number;
  reasonCodes?: string[];
  /** Prefer in queue Reason column (AI media tags for proactive review). */
  reasonLabels?: string[];
  /** Second-step reason (impersonation who / hacked what). */
  reasonSubtype?: string | null;
  /** ScoutUs user named in a tree answer (impersonation of Someone on ScoutUs). */
  relatedUserId?: number | null;
  relatedUserName?: string | null;
  relatedUserHandle?: string | null;
  relatedUserSlug?: string | null;
  relatedUserType?: string | null;
  relatedUserProfileImage?: string | null;
  /** Lifetime reports this reporter filed that were actioned (not dismissed). */
  reporterActionedCount?: number;
  flagCount?: number;
  targetUserProfileImage?: string | null;
  relatedModeration?: RelatedModeration | null;
  reporters?: Array<{
    reportId: number;
    userId: number;
    name: string | null;
    userType: string | null;
    reasonCode: string;
    reasonText: string | null;
    createdAt: string | Date;
    flagCount?: number;
    reasons?: Array<{
      reportId: number;
      reasonCode: string;
      reasonText: string | null;
      createdAt: string | Date;
    }>;
  }>;
  contentPreview?: {
    text: string | null;
    deleted: boolean;
    unavailable: boolean;
    mediaIds?: number[];
    media?: Array<{
      id: number;
      width: number | null;
      height: number | null;
      url?: string | null;
      thumbnailUrl?: string | null;
      delivery?: "mux" | "url" | null;
      moderationStatus?: string | null;
      aiTags?: Array<{ key: string; label: string }>;
      aiProvider?: string | null;
      isInappropriate?: boolean;
      purpose?: string | null;
      isPublic?: boolean;
      moderatedAt?: string | Date | null;
    }>;
  } | null;
  contentCreatedAt?: string | Date | null;
  contentUpdatedAt?: string | Date | null;
  priorResolvedAt?: string | Date | null;
  reopenedAfterContentChange?: boolean;
  targetViolationCount?: number;
  targetActiveStrikeCount?: number;
  /** Live account status + ban/suspend lock for the reported user. */
  targetAccountLock?: {
    status: string;
    kind:
      | "banned"
      | "suspended"
      | "locked"
      | "active"
      | "deactivated"
      | "pending_deletion"
      | "deleted"
      | "pending"
      | "other";
    action: string | null;
    suspendDays: number | null;
    expiresAt: string | Date | null;
    package: string | null;
    policyTag: string | null;
  } | null;
  resolutionNote?: string | null;
  enforcement?: {
    id: number;
    action: string;
    package?: string | null;
    removeContent?: boolean;
    strikeDelta?: number;
    suspendDays?: number | null;
    policyTag: string | null;
    note: string | null;
    createdAt: string | Date;
    expiresAt?: string | Date | null;
  } | null;
  enforcementHistory?: Array<{
    id: number;
    action: string;
    package?: string | null;
    removeContent?: boolean;
    strikeDelta?: number;
    suspendDays?: number | null;
    contentKey: string | null;
    targetType: string | null;
    policyTag: string | null;
    note: string | null;
    createdAt: string | Date;
    expiresAt?: string | Date | null;
    reportId?: number | null;
    actorUserId?: number | null;
    actorName?: string | null;
    contentLabel?: string | null;
    contentExcerpt?: string | null;
    mediaIds?: number[];
    caseSource?: "user_reports" | "ai_media_flag" | "direct";
    reasonCode?: string | null;
    flagCount?: number;
  }>;
  strikeHistory?: Array<{
    id: number;
    delta: number;
    policyTag: string | null;
    tier?: number | null;
    status: "active" | "expired" | "overturned" | string;
    createdAt: string | Date;
    expiresAt?: string | Date | null;
    enforcementId?: number | null;
    action?: string | null;
    package?: string | null;
    note?: string | null;
    suspendDays?: number | null;
    reportId?: number | null;
    actorUserId?: number | null;
    actorName?: string | null;
    contentKey?: string | null;
    targetType?: string | null;
    caseSource?: "user_reports" | "ai_media_flag" | "direct";
    reasonCode?: string | null;
    flagCount?: number;
    contentLabel?: string | null;
    contentExcerpt?: string | null;
    mediaIds?: number[];
  }>;
};

async function reportRequest<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await adminAxiosClient.request<T>({
    ...config,
    url: `/api/v1/admin/reports${config.url ?? ""}`,
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
    },
  });
  return response.data;
}

async function policyRuleRequest<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await adminAxiosClient.request<T>({
    ...config,
    url: `/api/v1/admin/moderation/policy-rules${config.url ?? ""}`,
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
    },
  });
  return response.data;
}

async function adminRequest<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await adminAxiosClient.request<T>({
    ...config,
    url: `/api/v1/admin${config.url ?? ""}`,
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
    },
  });
  return response.data;
}

export type AdminUserFeedAttachment = {
  type: "image" | "video" | string;
  assetUrl: string | null;
  thumbUrl: string | null;
  imageUrl: string | null;
  title: string | null;
};

export type AdminUserFeedPost = {
  id: string;
  text: string | null;
  createdAt: string | null;
  authorName: string | null;
  authorImage: string | null;
  attachments: AdminUserFeedAttachment[];
  imageCount: number;
  videoCount: number;
  attachmentCount: number;
};

export type AdminUserFeedPostsResponse = {
  userId: number;
  posts: AdminUserFeedPost[];
};

export type AdminUserConversationMessage = {
  id: string;
  text: string | null;
  createdAt: string | null;
  userId: string | null;
  userName: string | null;
  type: string | null;
};

export type AdminUserConversation = {
  id: string;
  cid: string;
  type: string;
  title: string;
  memberCount: number;
  members: Array<{ id: string; name: string | null; image: string | null }>;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  messages: AdminUserConversationMessage[];
};

export type AdminUserConversationsResponse = {
  userId: number;
  conversations: AdminUserConversation[];
};

export type AdminUserConversationMessagesResponse = {
  channelId: string;
  messages: AdminUserConversation["messages"];
};

export type AdminUserReviewMediaModerationEvent = {
  id: number;
  eventType: string;
  createdAt?: string | Date | null;
  userId?: number | null;
  moderationEventId?: number | null;
};

export type AdminUserReviewMediaItem = {
  id: number;
  moderation_status?: string | null;
  purpose?: string | null;
  file_name?: string | null;
  created_at?: string | null;
  urls?: {
    visible_url?: string | null;
    thumbnail_url?: string | null;
  } | null;
  /** Legacy / asset-queue decisions from media_moderation_event. */
  moderation_history?: AdminUserReviewMediaModerationEvent[];
};

export type AdminUserReviewMediaResponse = {
  media: AdminUserReviewMediaItem[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
};

class ModerationService {
  async getOverview(): Promise<ModerationOverviewDto> {
    return dedupeAsync(
      "moderation:overview",
      () => api.adminModeration.getOverview(),
      { ttlMs: 45_000 },
    );
  }

  async getTextRejections(
    params?: GetTextRejectionsParams,
  ): Promise<AdminTextRejectionListResponseDto> {
    return api.adminModeration.getTextRejections(params);
  }

  async getTextRejection(id: number): Promise<AdminTextRejectionDto> {
    return api.adminModeration.getTextRejection(id);
  }

  async resolveTextAppeal(
    id: number,
    dto: ResolveTextAppealDto,
  ): Promise<AdminTextRejectionDto> {
    return api.adminModeration.resolveTextAppeal(id, dto);
  }

  async getAccountAppeals(
    params?: GetAccountAppealsParams,
  ): Promise<AdminAccountAppealListResponse> {
    return adminRequest<AdminAccountAppealListResponse>({
      method: "GET",
      url: "/account-appeals",
      params,
    });
  }

  async getAccountAppeal(id: number): Promise<AdminAccountAppealDto> {
    return adminRequest<AdminAccountAppealDto>({
      method: "GET",
      url: `/account-appeals/${id}`,
    });
  }

  async resolveAccountAppeal(
    id: number,
    dto: ResolveAccountAppealDto,
  ): Promise<AdminAccountAppealDto> {
    return adminRequest<AdminAccountAppealDto>({
      method: "POST",
      url: `/account-appeals/${id}/resolve`,
      data: dto,
    });
  }

  async getReports(
    params?: GetReportsParamsExtended,
  ): Promise<AdminReportListResponseDto> {
    return api.adminReports.getReports(params as GetReportsParams);
  }

  async getReport(id: number): Promise<AdminReportExtended> {
    const response = await api.adminReports.getReportById(id);
    return response.data as AdminReportExtended;
  }

  async resolveReport(
    id: number,
    dto: ResolveReportDto,
  ): Promise<AdminReportExtended> {
    const response = await reportRequest<AdminReportResponseDto>({
      method: "POST",
      url: `/${id}/resolve`,
      data: dto,
    });
    return response.data as AdminReportExtended;
  }

  async enforceReport(
    id: number,
    dto: EnforceReportDto,
  ): Promise<AdminReportExtended> {
    const response = await reportRequest<AdminReportResponseDto>({
      method: "POST",
      url: `/${id}/enforce`,
      data: dto,
    });
    return response.data as AdminReportExtended;
  }

  /**
   * AI Flagged Media enforce — same packages as report enforce.
   * Dismiss approves the asset; remove_* / ban reject it + ledger effects.
   */
  async enforceMedia(
    mediaId: number,
    dto: EnforceReportDto,
  ): Promise<AdminReportExtended> {
    const response = await adminAxiosClient.request<AdminReportResponseDto>({
      method: "POST",
      url: `/api/v1/admin/moderation/media/${mediaId}/enforce`,
      data: dto,
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data.data as AdminReportExtended;
  }

  /** Parent-post enforcement / open-report signals for Media Verification. */
  async getRelatedModerationForMedia(
    mediaId: number,
  ): Promise<RelatedModeration | null> {
    const response = await adminAxiosClient.request<{
      data: RelatedModeration | null;
    }>({
      method: "GET",
      url: `/api/v1/admin/moderation/media/${mediaId}/related-moderation`,
    });
    return response.data.data ?? null;
  }

  async reopenReport(id: number, note?: string): Promise<AdminReportExtended> {
    const response = await reportRequest<AdminReportResponseDto>({
      method: "POST",
      url: `/${id}/reopen`,
      data: note?.trim() ? { note: note.trim() } : {},
    });
    return response.data as AdminReportExtended;
  }

  async getPolicyRules(
    params?: GetPolicyRulesParams,
  ): Promise<PolicyRuleListResponse> {
    return policyRuleRequest<PolicyRuleListResponse>({
      method: "GET",
      url: "",
      params: {
        scope: params?.scope ?? "report",
        includeArchived: params?.includeArchived ?? false,
      },
    });
  }

  async createPolicyRule(dto: CreatePolicyRuleDto): Promise<PolicyRuleDto> {
    return policyRuleRequest<PolicyRuleDto>({
      method: "POST",
      url: "",
      data: dto,
    });
  }

  async updatePolicyRule(
    id: number,
    dto: UpdatePolicyRuleDto,
  ): Promise<PolicyRuleDto> {
    return policyRuleRequest<PolicyRuleDto>({
      method: "PATCH",
      url: `/${id}`,
      data: dto,
    });
  }

  async archivePolicyRule(id: number): Promise<PolicyRuleDto> {
    return policyRuleRequest<PolicyRuleDto>({
      method: "POST",
      url: `/${id}/archive`,
    });
  }

  async restorePolicyRule(id: number): Promise<PolicyRuleDto> {
    return policyRuleRequest<PolicyRuleDto>({
      method: "POST",
      url: `/${id}/restore`,
    });
  }

  /** Stream feed activities for account-report review (right-rail context). */
  async getUserFeedPosts(
    userId: number,
    limit = 25,
  ): Promise<AdminUserFeedPostsResponse> {
    return adminRequest<AdminUserFeedPostsResponse>({
      method: "GET",
      url: `/users/${userId}/feed-posts`,
      params: { limit },
    });
  }

  /** Recent media uploads for account-report review. */
  async getUserReviewMedia(
    userId: number,
    page = 1,
    limit = 20,
  ): Promise<AdminUserReviewMediaResponse> {
    return adminRequest<AdminUserReviewMediaResponse>({
      method: "GET",
      url: `/users/${userId}/media`,
      params: { page, limit },
    });
  }

  /**
   * Stream Chat threads for account-report review.
   * Pass `withUserIds` (reporter ids) to only return DMs that also include them.
   */
  async getUserConversations(
    userId: number,
    limit = 25,
    withUserIds: number[] = [],
  ): Promise<AdminUserConversationsResponse> {
    return adminRequest<AdminUserConversationsResponse>({
      method: "GET",
      url: `/users/${userId}/conversations`,
      params: {
        limit,
        ...(withUserIds.length > 0
          ? { withUserIds: withUserIds.join(",") }
          : {}),
      },
    });
  }

  /** Longer message window for one channel (highlight reported message). */
  async getUserConversationMessages(
    userId: number,
    channelType: string,
    channelId: string,
    limit = 80,
  ): Promise<AdminUserConversationMessagesResponse> {
    return adminRequest<AdminUserConversationMessagesResponse>({
      method: "GET",
      url: `/users/${userId}/conversations/${encodeURIComponent(channelType)}/${encodeURIComponent(channelId)}/messages`,
      params: { limit },
    });
  }
}

export default ModerationService;
